import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { categories, blogPosts } from "../../data/blogData";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");

  // Refs for GSAP animations
  const heroTitleRef = useRef(null);
  const heroTextRef = useRef(null);
  const blogCardRefs = useRef([]);

  // Filter blog posts based on active category
  const filteredPosts =
    activeCategory === "all"
      ? blogPosts
      : blogPosts.filter((post) => post.category === activeCategory);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Helper function for parallax effect
    const createParallax = (element) => {
      if (!element) return;

      gsap.to(element, {
        yPercent: 20,
        ease: "none",
        scrollTrigger: {
          trigger: element.parentElement,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    };

    // Apply parallax to all images
    const images = document.querySelectorAll("img");
    images.forEach((img) => {
      img.style.transform = "scale(1.1)";
      createParallax(img);
    });

    // Hero section animations
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    tl.fromTo(
      heroTitleRef.current,
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 1 }
    ).fromTo(
      heroTextRef.current,
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 1 },
      "-=0.5"
    );

    // Blog cards animation
    gsap.fromTo(
      blogCardRefs.current,
      {
        y: 30,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: blogCardRefs.current[0],
          start: "top 80%",
        },
      }
    );
  }, [activeCategory]); // Re-run animations when category changes

  return (
    <div className="max-w-[1400px] mx-auto px-8">
      {/* Hero Section */}
      <section className="my-16">
        <div className="flex flex-col gap-4 mb-16">
          <h1
            ref={heroTitleRef}
            className="text-[40px] leading-[1.2] font-medium"
          >
            Architecture & Design Blog
          </h1>
          <p
            ref={heroTextRef}
            className="text-[15px] leading-relaxed text-gray-800 font-medium mt-2 font-serif"
          >
            Explore the latest trends, insights, and innovations in architecture
            and design.
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <div className="flex overflow-x-auto pb-2 gap-4 mb-12 hide-scrollbar">
        <div className="flex gap-4 px-4 md:px-0 md:justify-center w-full">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`px-6 py-2 text-sm font-medium transition-all whitespace-nowrap ${
                activeCategory === category.id
                  ? "bg-black text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-800"
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Add custom scrollbar hiding styles */}
      <style>
        {`
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>

      {/* Blog Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
        {filteredPosts.map((post, index) => (
          <Link
            to={`/blog/${post.slug}`}
            key={post.id}
            ref={(el) => (blogCardRefs.current[index] = el)}
            className="group cursor-pointer"
          >
            <div className="aspect-[4/3] overflow-hidden mb-4">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover bg-center bg-no-repeat"
              />
            </div>
            <div className="space-y-2">
              <p className="text-sm text-gray-600 uppercase tracking-wider">
                {post.category}
              </p>
              <h3 className="text-xl font-medium group-hover:text-gray-600 transition-colors">
                {post.title}
              </h3>
              <p className="text-gray-600 line-clamp-2">{post.description}</p>
              <div className="flex items-center gap-2 text-sm text-gray-600 mt-4">
                <span>{post.author}</span>
                <span>•</span>
                <span>{post.date}</span>
                <span>•</span>
                <span>{post.readTime}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* No Results Message */}
      {filteredPosts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600 text-lg">
            No posts found in this category.
          </p>
        </div>
      )}
    </div>
  );
};

export default BlogPage;
